import { nextId } from './ids';
import { Vec2 } from './math';
import { closestPointOnSegment } from './query';
import type { Entity, EntityId } from './types';

/**
 * Constraint layer for procedural modelling.
 *
 * A constraint always names its geometry by *handle* (`a`, `b`, `v3`, `c`, …) rather
 * than by coordinates, so it survives edits and can be re-solved. Snaps are stored as
 * constraints too: a vertex snap becomes `coincident`, an on-edge snap becomes
 * `pointOnEdge`, and a free-space snap becomes `anchor`.
 */

export interface PointRef {
  entityId: EntityId;
  /** Handle name inside the entity (`a`, `b`, `c`, `p`, `p1`, `p2`, `v<i>`). */
  handle: string;
}

export interface EdgeRef {
  entityId: EntityId;
  from: string;
  to: string;
}

/** Where the constraint came from: an explicit command or an object snap. */
export type ConstraintSource = 'user' | 'snap';

interface ConstraintBase {
  id: string;
  source?: ConstraintSource;
  /** Snap flavour (`endpoint`, `center`, `midpoint`, `grid`, …) for display. */
  snap?: string;
}

export type Constraint =
  | (ConstraintBase & { kind: 'coincident'; point: PointRef; target: PointRef })
  | (ConstraintBase & { kind: 'anchor'; point: PointRef; at: Vec2 })
  | (ConstraintBase & { kind: 'fix'; point: PointRef })
  | (ConstraintBase & { kind: 'distance'; point: PointRef; target: PointRef; value: number })
  | (ConstraintBase & { kind: 'horizontal'; edge: EdgeRef })
  | (ConstraintBase & { kind: 'vertical'; edge: EdgeRef })
  | (ConstraintBase & { kind: 'parallel'; edge: EdgeRef; other: EdgeRef })
  | (ConstraintBase & { kind: 'perpendicular'; edge: EdgeRef; other: EdgeRef })
  | (ConstraintBase & { kind: 'pointOnEdge'; point: PointRef; edge: EdgeRef });

export type ConstraintKind = Constraint['kind'];

export interface HandlePoint {
  name: string;
  point: Vec2;
}

type WithoutId<T> = T extends unknown ? Omit<T, 'id'> & { id?: string } : never;

/** Creates a constraint and assigns an id when the caller did not supply one. */
export function makeConstraint(constraint: WithoutId<Constraint>): Constraint {
  const id = (constraint as { id?: string }).id ?? nextId('k');
  return { ...constraint, id } as Constraint;
}

// ------------------------------------------------------------------- handles

export function entityHandles(entity: Entity): HandlePoint[] {
  switch (entity.type) {
    case 'line':
      return [
        { name: 'a', point: entity.a },
        { name: 'b', point: entity.b },
      ];
    case 'polyline':
      return entity.vertices.map((point, index) => ({ name: `v${index}`, point }));
    case 'spline':
      return entity.controlPoints.map((point, index) => ({ name: `v${index}`, point }));
    case 'circle':
    case 'arc':
    case 'ellipse':
      return [{ name: 'c', point: entity.center }];
    case 'text':
      return [{ name: 'p', point: entity.position }];
    case 'dimension':
      return [
        { name: 'p1', point: entity.p1 },
        { name: 'p2', point: entity.p2 },
      ];
    default:
      return [];
  }
}

export function entityPoint(entity: Entity, handle: string): Vec2 | null {
  return entityHandles(entity).find((h) => h.name === handle)?.point ?? null;
}

/** Returns a copy of `entity` with one handle moved. Entities stay immutable. */
export function withHandlePoint(entity: Entity, handle: string, point: Vec2): Entity {
  switch (entity.type) {
    case 'line':
      if (handle === 'a') return { ...entity, a: point };
      if (handle === 'b') return { ...entity, b: point };
      return entity;
    case 'polyline': {
      const index = Number(handle.slice(1));
      if (!Number.isInteger(index) || index < 0 || index >= entity.vertices.length) return entity;
      const vertices = [...entity.vertices];
      vertices[index] = point;
      return { ...entity, vertices };
    }
    case 'spline': {
      const index = Number(handle.slice(1));
      if (!Number.isInteger(index) || index < 0 || index >= entity.controlPoints.length) return entity;
      const controlPoints = [...entity.controlPoints];
      controlPoints[index] = point;
      return { ...entity, controlPoints };
    }
    case 'circle':
    case 'arc':
    case 'ellipse':
      return handle === 'c' ? { ...entity, center: point } : entity;
    case 'text':
      return handle === 'p' ? { ...entity, position: point } : entity;
    case 'dimension':
      if (handle === 'p1') return { ...entity, p1: point };
      if (handle === 'p2') return { ...entity, p2: point };
      return entity;
    default:
      return entity;
  }
}

export function entityEdges(entity: Entity): EdgeRef[] {
  if (entity.type === 'line') return [{ entityId: entity.id, from: 'a', to: 'b' }];
  if (entity.type === 'polyline' || entity.type === 'spline') {
    const count =
      entity.type === 'polyline' ? entity.vertices.length : entity.controlPoints.length;
    const edges: EdgeRef[] = [];
    for (let i = 1; i < count; i++) {
      edges.push({ entityId: entity.id, from: `v${i - 1}`, to: `v${i}` });
    }
    if (entity.closed && count > 2) {
      edges.push({ entityId: entity.id, from: `v${count - 1}`, to: 'v0' });
    }
    return edges;
  }
  return [];
}

export function samePoint(a: PointRef, b: PointRef): boolean {
  return a.entityId === b.entityId && a.handle === b.handle;
}

export function sameEdge(a: EdgeRef, b: EdgeRef): boolean {
  if (a.entityId !== b.entityId) return false;
  return (a.from === b.from && a.to === b.to) || (a.from === b.to && a.to === b.from);
}

/** Every entity a constraint pulls on. */
export function constraintEntityIds(constraint: Constraint): EntityId[] {
  switch (constraint.kind) {
    case 'coincident':
    case 'distance':
      return unique([constraint.point.entityId, constraint.target.entityId]);
    case 'anchor':
    case 'fix':
      return [constraint.point.entityId];
    case 'horizontal':
    case 'vertical':
      return [constraint.edge.entityId];
    case 'parallel':
    case 'perpendicular':
      return unique([constraint.edge.entityId, constraint.other.entityId]);
    case 'pointOnEdge':
      return unique([constraint.point.entityId, constraint.edge.entityId]);
  }
}

export function describeConstraint(constraint: Constraint): string {
  switch (constraint.kind) {
    case 'coincident':
      return `coincident ${refLabel(constraint.point)} → ${refLabel(constraint.target)}`;
    case 'anchor':
      return `anchor ${refLabel(constraint.point)} at ${constraint.at.x}, ${constraint.at.y}`;
    case 'fix':
      return `fixed ${refLabel(constraint.point)}`;
    case 'distance':
      return `distance ${refLabel(constraint.point)} → ${refLabel(constraint.target)} = ${constraint.value}`;
    case 'horizontal':
      return `horizontal ${constraint.edge.entityId}:${constraint.edge.from}-${constraint.edge.to}`;
    case 'vertical':
      return `vertical ${constraint.edge.entityId}:${constraint.edge.from}-${constraint.edge.to}`;
    case 'parallel':
      return 'parallel edges';
    case 'perpendicular':
      return 'perpendicular edges';
    case 'pointOnEdge':
      return `on-edge ${refLabel(constraint.point)}`;
  }
}

export function refLabel(ref: PointRef): string {
  return `${ref.entityId}:${ref.handle}`;
}

// -------------------------------------------------------------- nearest picks

export interface HandleHit {
  ref: PointRef;
  point: Vec2;
  distance: number;
}

/** Closest vertex/centre handle to `point`, used to turn a pick into a point constraint. */
export function nearestHandle(
  entities: readonly Entity[],
  point: Vec2,
  tolerance: number,
): HandleHit | null {
  let best: HandleHit | null = null;
  for (const entity of entities) {
    for (const handle of entityHandles(entity)) {
      const distance = handle.point.distanceTo(point);
      if (distance > tolerance) continue;
      if (!best || distance < best.distance) {
        best = { ref: { entityId: entity.id, handle: handle.name }, point: handle.point, distance };
      }
    }
  }
  return best;
}

export interface EdgeHit {
  edge: EdgeRef;
  point: Vec2;
  distance: number;
}

/** Closest line/polyline segment to `point`. */
export function nearestEdge(
  entities: readonly Entity[],
  point: Vec2,
  tolerance: number,
): EdgeHit | null {
  let best: EdgeHit | null = null;
  for (const entity of entities) {
    for (const edge of entityEdges(entity)) {
      const from = entityPoint(entity, edge.from);
      const to = entityPoint(entity, edge.to);
      if (!from || !to) continue;
      const projected = projectOnSegment(from, to, point);
      if (!projected) continue;
      const distance = projected.distanceTo(point);
      if (distance > tolerance) continue;
      if (!best || distance < best.distance) {
        best = { edge, point: projected, distance };
      }
    }
  }
  return best;
}

function projectOnSegment(a: Vec2, b: Vec2, p: Vec2): Vec2 | null {
  const ab = b.sub(a);
  const lenSq = ab.lenSq();
  if (lenSq < 1e-18) return null;
  const t = Math.min(1, Math.max(0, p.sub(a).dot(ab) / lenSq));
  return a.add(ab.scale(t));
}

// ------------------------------------------------------------------- solver

export interface SolveResult {
  /** Updated copies of the entities that moved. */
  entities: Entity[];
  /** Largest remaining constraint error in millimetres (or sine of an angle error). */
  residual: number;
  satisfied: number;
  total: number;
}

interface Node {
  entityId: EntityId;
  handle: string;
  x: number;
  y: number;
  pinned: boolean;
}

const DEFAULT_ITERATIONS = 80;

/**
 * Relaxation solver: every constraint projects the points it owns, sweeping until the
 * error stops improving. Pinned points (fixed anchors) never move, so constraints act
 * like a rubber sheet that pulls the free geometry into shape.
 */
export function solveConstraints(
  entities: readonly Entity[],
  constraints: readonly Constraint[],
  iterations = DEFAULT_ITERATIONS,
): SolveResult {
  const byId = new Map<EntityId, Entity>();
  for (const entity of entities) byId.set(entity.id, entity);

  const nodes = new Map<string, Node>();
  const original = new Map<string, Vec2>();
  const node = (ref: PointRef): Node | null => {
    const key = nodeKey(ref);
    const existing = nodes.get(key);
    if (existing) return existing;
    const entity = byId.get(ref.entityId);
    if (!entity) return null;
    const point = entityPoint(entity, ref.handle);
    if (!point) return null;
    const created: Node = {
      entityId: ref.entityId,
      handle: ref.handle,
      x: point.x,
      y: point.y,
      pinned: false,
    };
    nodes.set(key, created);
    original.set(key, point);
    return created;
  };

  const live: Constraint[] = [];
  for (const constraint of constraints) {
    if (isSolvable(constraint, node)) live.push(constraint);
  }

  for (const constraint of live) {
    if (constraint.kind === 'anchor') {
      const target = node(constraint.point);
      if (!target) continue;
      target.x = constraint.at.x;
      target.y = constraint.at.y;
      target.pinned = true;
    } else if (constraint.kind === 'fix') {
      const target = node(constraint.point);
      if (target) target.pinned = true;
    }
  }

  const free = [...nodes.values()].some((n) => !n.pinned);
  if (free && live.length > 0) {
    for (let pass = 0; pass < iterations; pass++) {
      for (const constraint of live) project(constraint, node);
    }
  }

  const residual = live.reduce((worst, c) => Math.max(worst, error(c, node)), 0);
  const satisfied = live.filter((c) => error(c, node) <= 1e-6).length;

  const moved = new Map<EntityId, Node[]>();
  for (const [key, current] of nodes) {
    const before = original.get(key);
    if (!before) continue;
    if (Math.abs(before.x - current.x) < 1e-9 && Math.abs(before.y - current.y) < 1e-9) continue;
    const list = moved.get(current.entityId);
    if (list) list.push(current);
    else moved.set(current.entityId, [current]);
  }

  const updated: Entity[] = [];
  for (const [entityId, list] of moved) {
    const source = byId.get(entityId);
    if (!source) continue;
    let copy: Entity = source;
    for (const item of list) copy = withHandlePoint(copy, item.handle, Vec2.of(item.x, item.y));
    updated.push(copy);
  }

  return { entities: updated, residual, satisfied, total: live.length };
}

function isSolvable(constraint: Constraint, node: (ref: PointRef) => Node | null): boolean {
  switch (constraint.kind) {
    case 'coincident':
    case 'distance':
      return Boolean(node(constraint.point) && node(constraint.target));
    case 'anchor':
    case 'fix':
      return Boolean(node(constraint.point));
    case 'horizontal':
    case 'vertical':
      return Boolean(node({ entityId: constraint.edge.entityId, handle: constraint.edge.from }) &&
        node({ entityId: constraint.edge.entityId, handle: constraint.edge.to }));
    case 'parallel':
    case 'perpendicular':
      return Boolean(edgeNodes(constraint.edge, node) && edgeNodes(constraint.other, node));
    case 'pointOnEdge':
      return Boolean(node(constraint.point) && edgeNodes(constraint.edge, node));
  }
}

function edgeNodes(
  edge: EdgeRef,
  node: (ref: PointRef) => Node | null,
): [Node, Node] | null {
  const from = node({ entityId: edge.entityId, handle: edge.from });
  const to = node({ entityId: edge.entityId, handle: edge.to });
  return from && to ? [from, to] : null;
}

function nodeKey(ref: PointRef): string {
  return `${ref.entityId}\u0000${ref.handle}`;
}

function move(node: Node, x: number, y: number): void {
  if (node.pinned) return;
  node.x = x;
  node.y = y;
}

function project(constraint: Constraint, node: (ref: PointRef) => Node | null): void {
  switch (constraint.kind) {
    case 'coincident': {
      const a = node(constraint.point);
      const b = node(constraint.target);
      if (!a || !b) return;
      if (!a.pinned && !b.pinned) {
        const x = (a.x + b.x) / 2;
        const y = (a.y + b.y) / 2;
        move(a, x, y);
        move(b, x, y);
      } else if (!a.pinned) {
        move(a, b.x, b.y);
      } else {
        move(b, a.x, a.y);
      }
      return;
    }
    case 'distance': {
      const a = node(constraint.point);
      const b = node(constraint.target);
      if (!a || !b) return;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const length = Math.hypot(dx, dy);
      if (length < 1e-9) return;
      const scale = constraint.value / length;
      if (!a.pinned && !b.pinned) {
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        const hx = (dx * scale) / 2;
        const hy = (dy * scale) / 2;
        move(a, mx - hx, my - hy);
        move(b, mx + hx, my + hy);
      } else if (!a.pinned) {
        move(a, b.x - dx * scale, b.y - dy * scale);
      } else {
        move(b, a.x + dx * scale, a.y + dy * scale);
      }
      return;
    }
    case 'horizontal': {
      const pair = edgeNodes(constraint.edge, node);
      if (!pair) return;
      const [a, b] = pair;
      if (a.pinned && b.pinned) return;
      if (a.pinned) move(b, b.x, a.y);
      else if (b.pinned) move(a, a.x, b.y);
      else {
        const y = (a.y + b.y) / 2;
        move(a, a.x, y);
        move(b, b.x, y);
      }
      return;
    }
    case 'vertical': {
      const pair = edgeNodes(constraint.edge, node);
      if (!pair) return;
      const [a, b] = pair;
      if (a.pinned && b.pinned) return;
      if (a.pinned) move(b, a.x, b.y);
      else if (b.pinned) move(a, b.x, a.y);
      else {
        const x = (a.x + b.x) / 2;
        move(a, x, a.y);
        move(b, x, b.y);
      }
      return;
    }
    case 'pointOnEdge': {
      const p = node(constraint.point);
      const pair = edgeNodes(constraint.edge, node);
      if (!p || !pair) return;
      const [a, b] = pair;
      const abx = b.x - a.x;
      const aby = b.y - a.y;
      const lenSq = abx * abx + aby * aby;
      if (lenSq < 1e-18) return;
      const t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq;
      move(p, a.x + abx * t, a.y + aby * t);
      return;
    }
    case 'parallel': {
      const first = edgeNodes(constraint.edge, node);
      const second = edgeNodes(constraint.other, node);
      if (!first || !second) return;
      const error = normalizeAngle(angleOf(first) - angleOf(second));
      rotateEdge(first, -error / 2);
      rotateEdge(second, error / 2);
      return;
    }
    case 'perpendicular': {
      const first = edgeNodes(constraint.edge, node);
      const second = edgeNodes(constraint.other, node);
      if (!first || !second) return;
      const error = normalizeAngle(angleOf(first) - angleOf(second) - Math.PI / 2);
      rotateEdge(first, -error / 2);
      rotateEdge(second, error / 2);
      return;
    }
    default:
      return;
  }
}

function angleOf(pair: [Node, Node]): number {
  return Math.atan2(pair[1].y - pair[0].y, pair[1].x - pair[0].x);
}

/** Wraps an angle difference into (-π/2, π/2]; edges are undirected. */
function normalizeAngle(value: number): number {
  return value - Math.PI * Math.round(value / Math.PI);
}

function rotateEdge(pair: [Node, Node], delta: number): void {
  const [a, b] = pair;
  if (!Number.isFinite(delta) || Math.abs(delta) < 1e-12) return;
  if (a.pinned && b.pinned) return;
  if (a.pinned) {
    const v = Vec2.of(b.x - a.x, b.y - a.y).rotate(delta);
    move(b, a.x + v.x, a.y + v.y);
    return;
  }
  if (b.pinned) {
    const v = Vec2.of(a.x - b.x, a.y - b.y).rotate(delta);
    move(a, b.x + v.x, b.y + v.y);
    return;
  }
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const va = Vec2.of(a.x - mx, a.y - my).rotate(delta);
  const vb = Vec2.of(b.x - mx, b.y - my).rotate(delta);
  move(a, mx + va.x, my + va.y);
  move(b, mx + vb.x, my + vb.y);
}

function error(constraint: Constraint, node: (ref: PointRef) => Node | null): number {
  switch (constraint.kind) {
    case 'coincident': {
      const a = node(constraint.point);
      const b = node(constraint.target);
      return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
    }
    case 'anchor': {
      const a = node(constraint.point);
      return a ? Math.hypot(a.x - constraint.at.x, a.y - constraint.at.y) : 0;
    }
    case 'fix':
      return 0;
    case 'distance': {
      const a = node(constraint.point);
      const b = node(constraint.target);
      return a && b ? Math.abs(Math.hypot(a.x - b.x, a.y - b.y) - constraint.value) : 0;
    }
    case 'horizontal': {
      const pair = edgeNodes(constraint.edge, node);
      return pair ? Math.abs(pair[0].y - pair[1].y) : 0;
    }
    case 'vertical': {
      const pair = edgeNodes(constraint.edge, node);
      return pair ? Math.abs(pair[0].x - pair[1].x) : 0;
    }
    case 'pointOnEdge': {
      const p = node(constraint.point);
      const pair = edgeNodes(constraint.edge, node);
      if (!p || !pair) return 0;
      const [a, b] = pair;
      const abx = b.x - a.x;
      const aby = b.y - a.y;
      const length = Math.hypot(abx, aby);
      if (length < 1e-12) return 0;
      return Math.abs(((p.x - a.x) * aby - (p.y - a.y) * abx) / length);
    }
    case 'parallel': {
      const first = edgeNodes(constraint.edge, node);
      const second = edgeNodes(constraint.other, node);
      if (!first || !second) return 0;
      return Math.abs(normalizeAngle(angleOf(first) - angleOf(second)));
    }
    case 'perpendicular': {
      const first = edgeNodes(constraint.edge, node);
      const second = edgeNodes(constraint.other, node);
      if (!first || !second) return 0;
      return Math.abs(normalizeAngle(angleOf(first) - angleOf(second) - Math.PI / 2));
    }
    default:
      return 0;
  }
}

function unique(values: EntityId[]): EntityId[] {
  return [...new Set(values)];
}

/**
 * Where a circle's centre must sit so that it touches every one of `lines` at `radius`, and every
 * one of `rounds` too.
 *
 * The contact is **anywhere on the line**, not at a named grip: each demand offsets the centre
 * along the shortest direction from the segment to it, so a bore slides to the nearest point of the
 * edge it must meet and stays on the side it is already on. Off the end of the segment the shortest
 * direction runs to that end, which is tangency at the end of the line rather than to its
 * extension. Solving one edge at a time would walk back off the previous edge, which is exactly what
 * makes two tangents on one circle look broken — hence the demands are answered together.
 *
 * One or two edges are closed-form: a single offset, or the point where two offset lines cross. A
 * round target in the mix, or more edges than a corner can answer, has no closed form, so the
 * centre is projected onto each locus in turn until it stops moving. That is a best fit when the
 * demands cannot all be met (a circle cannot touch all four sides of a plate), but it never quietly
 * drops a demand the way taking them one at a time did.
 *
 * A round target can be met from outside (`r1 + r2` between the centres) or from inside
 * (`|r1 - r2|`). The nearer arrangement is tried first, but a bore drawn on top of the very circle
 * it must touch has no bearing to choose from, and two tangents can disagree — so every
 * arrangement is tried and the one that actually fits wins.
 */
export function tangentCentre(
  centre: Vec2,
  radius: number,
  lines: ReadonlyArray<readonly [Vec2, Vec2]>,
  rounds: ReadonlyArray<{ centre: Vec2; radius: number }> = [],
): Vec2 | null {
  const demands: Array<{ normal: Vec2; at: number }> = [];
  for (const [a, b] of lines) {
    const direction = b.sub(a);
    const span = Math.hypot(direction.x, direction.y);
    if (span < 1e-9) continue;
    const nearest = closestPointOnSegment(a, b, centre);
    const away = centre.sub(nearest);
    const length = Math.hypot(away.x, away.y);
    // A centre sitting exactly on the line has no side yet: use the perpendicular.
    const normal = length < 1e-9 ? Vec2.of(-direction.y / span, direction.x / span) : away.scale(1 / length);
    demands.push({ normal, at: nearest.x * normal.x + nearest.y * normal.y + Math.abs(radius) });
  }
  const first = demands[0];
  if (!first && rounds.length === 0) return null;

  const offsetOf = (demand: { normal: Vec2; at: number }): number =>
    demand.at - (centre.x * demand.normal.x + centre.y * demand.normal.y);
  const second = demands[1];

  if (rounds.length === 0 && !second) return centre.add(first!.normal.scale(offsetOf(first!)));
  if (rounds.length === 0 && demands.length === 2) {
    const a = first!;
    const b = second;
    const det = a.normal.x * b.normal.y - a.normal.y * b.normal.x;
    // Parallel edges have no corner to file into; honour the first demand and leave it there.
    if (Math.abs(det) < 1e-9) return centre.add(a.normal.scale(offsetOf(a)));
    return Vec2.of(
      (a.at * b.normal.y - b.at * a.normal.y) / det,
      (a.normal.x * b.at - b.normal.x * a.at) / det,
    );
  }

  // Every arrangement of inside/outside for the round targets, nearest-first order kept. Two round
  // targets are the practical limit, so the search stays at four attempts.
  let attempts: number[][] = [[]];
  rounds.forEach((round, index) => {
    const reaches = roundReaches(centre, radius, round.centre, round.radius);
    const offer = index < 2 ? reaches : reaches.slice(0, 1);
    const grown: number[][] = [];
    for (const prefix of attempts) for (const value of offer) grown.push([...prefix, value]);
    attempts = grown;
  });

  const violation = (point: Vec2, chosen: readonly number[]): number => {
    let worst = 0;
    for (const demand of demands) {
      worst = Math.max(worst, Math.abs(point.x * demand.normal.x + point.y * demand.normal.y - demand.at));
    }
    rounds.forEach((round, index) => {
      const gap = Math.hypot(point.x - round.centre.x, point.y - round.centre.y);
      worst = Math.max(worst, Math.abs(gap - chosen[index]!));
    });
    return worst;
  };

  let best: { point: Vec2; error: number } | null = null;
  for (const chosen of attempts) {
    // The offsets above are fixed planes, so the projection cannot drift onto the far side of an
    // edge mid-solve; the side each demand was on when it was read is the side it keeps.
    let solved = centre;
    for (let step = 0; step < 400; step++) {
      let moved = 0;
      for (const demand of demands) {
        const next = solved.add(
          demand.normal.scale(demand.at - (solved.x * demand.normal.x + solved.y * demand.normal.y)),
        );
        moved = Math.max(moved, next.distanceTo(solved));
        solved = next;
      }
      rounds.forEach((round, index) => {
        const next = slideOnto(solved, round.centre, chosen[index]!);
        moved = Math.max(moved, next.distanceTo(solved));
        solved = next;
      });
      if (moved < 1e-10) break;
    }
    const error = violation(solved, chosen);
    if (!best || error < best.error) best = { point: solved, error };
    if (error < 1e-7) break;
  }
  return best ? best.point : centre;
}

/** The distances a circle can touch another one from, the arrangement it is already in first. */
function roundReaches(centre: Vec2, radius: number, otherCentre: Vec2, otherRadius: number): number[] {
  const reach = Math.abs(radius);
  const other = Math.abs(otherRadius);
  const outside = reach + other;
  const inside = Math.abs(reach - other);
  if (Math.abs(outside - inside) < 1e-9) return [outside];
  const distance = Math.hypot(centre.x - otherCentre.x, centre.y - otherCentre.y);
  // Concentric: there is no bearing to prefer one from, so outside goes first.
  if (distance < 1e-9) return [outside, inside];
  return Math.abs(distance - outside) <= Math.abs(distance - inside)
    ? [outside, inside]
    : [inside, outside];
}

/** The centre at `reach` from `other`, along the bearing it already has — or +X when it has none. */
function slideOnto(centre: Vec2, other: Vec2, reach: number): Vec2 {
  const away = centre.sub(other);
  const distance = Math.hypot(away.x, away.y);
  if (distance < 1e-9) return other.add(Vec2.of(reach, 0));
  return other.add(away.scale(reach / distance));
}

/**
 * Tangency to another circle: the two touch when their centres are `r1 + r2` apart (outside) or
 * `|r1 - r2|` apart (one inside the other). Whichever of those two the circle is already nearer to
 * is the one it keeps, so a bore next to a boss hugs its outside and a bore inside a hole stays in.
 */
export function tangentToCircleCentre(
  centre: Vec2,
  radius: number,
  otherCentre: Vec2,
  otherRadius: number,
): Vec2 | null {
  const away = centre.sub(otherCentre);
  const distance = Math.hypot(away.x, away.y);
  if (distance < 1e-9) return null;
  const [reach] = roundReaches(centre, radius, otherCentre, otherRadius);
  return otherCentre.add(away.scale(reach! / distance));
}
